import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { c, t } from "ttag";

import iconsStarOutline from "../../../img/icons/star-outline.svg";
import iconsStar from "../../../img/icons/star.svg";
import { ReviewSnippet } from "../../models/tempur-love-grid";
import { CategoriesFooter } from "../CategoriesFooter";
import { useImageLoadRef } from "../GridTile";

type Props = Immutable<{
    data: ReviewSnippet;
}> & {
    ref?: React.Ref<HTMLAnchorElement>;
};

export const ReviewSnippetTile = (props: Props): React.JSX.Element => {
    const review = props.data.value;
    const onImageLoadRef = useImageLoadRef();

    // ReviewSnippet has no images, so mark as loaded immediately on mount
    React.useEffect(() => {
        onImageLoadRef.current?.();
        // Intentionally run only on mount - review snippets never load images
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <div className="post-review">
                <section className="post-copy">
                    <div className="star-rating">
                        <div className="star-rating__stars">
                            <span
                                className="ada-screenreader-only"
                                aria-label="star rating"
                            >
                                {c("review-snippet-tile")
                                    .t`Rated ${review.rating} out of 5 stars`}
                            </span>
                            {[1, 2, 3, 4, 5].map((num) => (
                                <SVG
                                    key={num}
                                    role="presentation"
                                    className="star-rating__star"
                                    src={
                                        review.rating >= num
                                            ? iconsStar
                                            : iconsStarOutline
                                    }
                                    aria-hidden="true"
                                />
                            ))}
                        </div>
                    </div>
                    <h2 className="post-review__title">{review.title}</h2>

                    <hr className="post-review__hr" />

                    <div
                        className="post-copy__body"
                        dangerouslySetInnerHTML={{ __html: review.content }}
                    />

                    <a
                        href={review.link_url}
                        ref={props.ref}
                        className="post-link"
                    >
                        {t`Read all reviews`}
                    </a>

                    <CategoriesFooter
                        categories={review.categories?.map((category) => {
                            if (category.slug === "reviews") {
                                return {
                                    ...category,
                                    override_url: review.link_url,
                                };
                            } else {
                                return category;
                            }
                        })}
                    />
                </section>
            </div>
        </>
    );
};
